import React, { useMemo } from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker, TimePicker } from "@material-ui/pickers";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { useDispatch, useSelector } from "react-redux";
import { getSelects } from "@icarius-pages/issuePermissions/selectors";
import { Autocomplete } from "@material-ui/lab";
import { createIssuePermissionAction, updateIssuePermissionAction } from "@icarius-pages/issuePermissions/actions";
import { RESOURCES_ENDPOINT } from "@icarius-connection/endpoints";
import { previewFromURL } from "@icarius-utils/download";
import { VisibilityIcon } from "@icarius-icons/index";
import FileInput from "@icarius-common/fileUpload/fileInput";
import FileList from "@icarius-common/fileUpload/fileList";
import { makeFile } from "@icarius-utils/fileUpload";
import useHandleForm from "./useHandleForm";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { height: 75, padding: "0px 10px" };

const CreateEditDialog = (props) => {

  const {
    open,
    isLoading,
    data,
    presetEmployee,
    handleClose,
    handleOpenBalanceDialog,
  } = props;

  const dispatch = useDispatch();
  const selects = useSelector(getSelects);

  const {
    ratings,
    employees,
    permissionTypes,
    units,
    timePerDayTypes,
  } = selects;

  const create = (dataToSend) => {
    dispatch(createIssuePermissionAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (dataToSend) => {
    dispatch(updateIssuePermissionAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const openTimePerDayValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("timePerDayCantExceedHours", { maxHours: formData['Código de empleado'].maxHours }), severity: "error", duration: 10000 }));
  }

  const openInvalidFileRequiredError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("forThisPermissionTypeAttachmentUploadIsRequired"), severity: "error" }));
  }

  const getMaxHoursLabel = () => {
    if (!formData['Código de empleado']) return "";
    return `${getLocalizedString("maxAllowed")}: ${formData['Código de empleado'].maxHours}`;
  }

  const getPermissionTypeSelected = (id) => {
    return permissionTypes.find((type) => type.key === id);
  }


  const {
    isCreate,
    formData,
    setFormValue,
    submit,
  } = useHandleForm(data, create, modify, openValidationError, openTimePerDayValidationError, openInvalidFileRequiredError, selects, presetEmployee);

  const handleSetFile = (files) => {
    let file = files[0];

    if (!file) {
      handleDeleteFile();
      return;
    }

    makeFile(file, (base64) => {
      setFormValue({ file: base64, name: file.name }, "attachment");
    })
  }

  const handleDeleteFile = () => {
    setFormValue({ file: null, name: "" }, "attachment")
  }

  const shouldShowBalanceButton = formData['Calificación'] === "C" && formData['Código de empleado'];

  const filteredEmployees = useMemo(() => {
    if (!employees) return [];
    if (presetEmployee) return employees.filter(item => item.key === presetEmployee);
    return employees;
  }, [employees, presetEmployee])

  if (isLoading) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {isCreate ? getLocalizedString("createPermission") : getLocalizedString("editPermission")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent>
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <Autocomplete
                disabled={!isCreate}
                style={{ width: '100%' }}
                options={filteredEmployees}
                value={formData['Código de empleado']}
                onChange={(event, newUser) => {
                  setFormValue(newUser, 'Código de empleado');
                }}
                noOptionsText={''}
                getOptionLabel={(user) => user.value}
                renderInput={(params) => <TextField required {...params} label={getLocalizedString("employee")} />}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-type`}>{getLocalizedString("permissionType")}</InputLabel>
                <Select
                  disabled={!isCreate}
                  value={formData['Tipo de permiso']}
                  labelId={`label-type`}
                  id={`select-type`}
                  onChange={(e) => setFormValue(e.target.value, 'Tipo de permiso')}
                  margin={"none"}
                >
                  {
                    permissionTypes.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  disabled={!isCreate}
                  fullWidth
                  required
                  label={getLocalizedString("delegationDateFrom")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="dd/MM/yyyy"
                  margin="none"
                  value={formData['Fecha desde']}
                  onChange={(e) => setFormValue(e, 'Fecha desde')}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}> {/* IPC */}
              <TextField
                disabled={!isCreate}
                required
                label={getLocalizedString("amountOfDays")}
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Cantidad de días']}
                onChange={(e) => {
                  if (e.target.value !== "") {
                    e.target.value = (Math.round(e.target.value * 100) / 100).toFixed(0);
                    setFormValue(e.target.value, 'Cantidad de días');
                  } else {
                    setFormValue(e.target.value, '')
                  }
                }}
                inputProps={{ min: "1", step: "1", max: "365" , isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 365}}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  fullWidth
                  disabled
                  label={getLocalizedString("delegationDateTo")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="dd/MM/yyyy"
                  margin="none"
                  value={formData['Fecha hasta']}
                  onChange={(e) => setFormValue(e, 'Fecha hasta')}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  disabled={!isCreate}
                  fullWidth
                  required
                  label={getLocalizedString("applicationStartDate")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="dd/MM/yyyy"
                  margin="none"
                  value={formData['Fecha desde de aplicación']}
                  onChange={(e) => setFormValue(e, 'Fecha desde de aplicación')}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  fullWidth
                  disabled
                  label={getLocalizedString("endDateOfApplication")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="dd/MM/yyyy"
                  margin="none"
                  value={formData['Fecha hasta de aplicación']}
                  onChange={(e) => setFormValue(e, 'Fecha hasta de aplicación')}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id={`label-unit`}>{getLocalizedString("dailyUnit")}</InputLabel>
                <Select
                  disabled
                  value={formData['Unidad diaria']}
                  labelId={`label-unit`}
                  id={`select-unit`}
                  onChange={(e) => setFormValue(e.target.value, 'Unidad diaria')}
                  margin={"none"}
                >
                  {
                    units.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              {
                formData['Unidad diaria'] === "D" ?
                  <FormControl style={{ width: "100%" }}>
                    <InputLabel required id={`label-timeperday`}>{getLocalizedString("timePerDay")}</InputLabel>
                    <Select
                      disabled={!isCreate}
                      value={formData['Tiempo por día']}
                      labelId={`label-timeperday`}
                      id={`select-timeperday`}
                      onChange={(e) => setFormValue(e.target.value, 'Tiempo por día')}
                      margin={"none"}
                    >
                      {
                        timePerDayTypes.map(item => (
                          <MenuItem
                            className={"whiteText"}
                            key={item.key}
                            value={item.key}>
                            {item.value}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                  :
                  <div style={{ width: '100%', marginBottom: formData['Código de empleado'] ? '-24px' : 0 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                      <TimePicker
                        disabled={!Boolean(formData['Unidad diaria']) || !isCreate}
                        fullWidth
                        required
                        minutesStep={5}
                        label={getLocalizedString("timePerDay")}
                        cancelLabel={getLocalizedString("cancel")}
                        okLabel={getLocalizedString("ok")}
                        invalidDateMessage=""
                        value={formData['Tiempo por día']}
                        onChange={(e) => setFormValue(e, 'Tiempo por día')}
                        format="HH:mm"
                        margin="none"
                        ampm={false}
                        helperText={getMaxHoursLabel()}
                      />
                    </MuiPickersUtilsProvider>
                  </div>
              }
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id={`label-class`}>{getLocalizedString("rating")}</InputLabel>
                <Select
                  disabled
                  value={formData['Calificación']}
                  labelId={`label-class`}
                  id={`select-class`}
                  onChange={(e) => setFormValue(e.target.value, 'Calificación')}
                  margin={"none"}
                >
                  {
                    ratings.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={4} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  fullWidth
                  disabled
                  label={getLocalizedString("returnDate")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="dd/MM/yyyy"
                  margin="none"
                  value={formData['Fecha de retorno']}
                  onChange={(e) => setFormValue(e, 'Fecha de retorno')}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <TextField
                label={getLocalizedString("comments")}
                fullWidth
                margin={"none"}
                value={formData['Comentarios']}
                onChange={(e) => setFormValue(e.target.value, "Comentarios")}
              />
            </Grid>
            {
              isCreate &&
              <Grid container item xs={12} alignItems="center" style={{ margin: 10 }}>
                <FileInput
                  required={formData['Tipo de permiso'] && getPermissionTypeSelected(formData['Tipo de permiso'])?.isAttachmentRequired}
                  acceptedFormats={['.pdf', '.bmp', '.png', '.jpg', '.jpeg']}
                  handleChange={handleSetFile}
                />
                <FileList
                  files={formData.attachment ? [{ name: formData.attachmentName }] : null}
                  handleClick={handleDeleteFile}
                />
              </Grid>
            }
            {
              !isCreate && data.attachment &&
              <Grid container item xs={12} alignItems="center" style={{ margin: 10 }}>
                <Tooltip title={getLocalizedString("viewAttachment")}>
                  <IconButton
                    style={{ maxHeight: 20, maxWidth: 20, margin: 10 }}
                    onClick={() => previewFromURL(RESOURCES_ENDPOINT + data.attachment)}
                  >
                    <VisibilityIcon fontSize="small" style={{ fill: "var(--mainText)" }} />
                  </IconButton>
                </Tooltip>
                <Typography style={{ fontSize: "0.9rem" }} className="whiteText" variant="caption">
                  {data.attachmentName}
                </Typography>
              </Grid>
            }
          </Grid>
          <Grid container item alignItems="center" xs={12} style={{ padding: 10, visibility: shouldShowBalanceButton ? 'visible' : 'hidden' }}>
            <Typography className="whiteText" style={{ textDecoration: "underline", cursor: 'pointer' }} onClick={() => handleOpenBalanceDialog(formData['Código de empleado'].key)}>
              {getLocalizedString("viewVacationAccountability")}
            </Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default CreateEditDialog;
